import React from 'react';
import { Box, Grid, Typography, Card, CardContent, ButtonGroup, Button } from '@mui/material';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getValidTenantId } from '../../utils/tenantUtils';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const TimeframeSelector = ({ timeframe, onChange }) => (
  <ButtonGroup size="small" aria-label="timeframe selection">
    <Button variant={timeframe === '1m' ? 'contained' : 'outlined'} onClick={() => onChange('1m')}>1M</Button>
    <Button variant={timeframe === '3m' ? 'contained' : 'outlined'} onClick={() => onChange('3m')}>3M</Button>
    <Button variant={timeframe === '6m' ? 'contained' : 'outlined'} onClick={() => onChange('6m')}>6M</Button>
    <Button variant={timeframe === '1y' ? 'contained' : 'outlined'} onClick={() => onChange('1y')}>1Y</Button>
  </ButtonGroup>
);

const CustomerSuccessAnalytics = ({ data, timeframe, onTimeframeChange }) => {
  // Make sure we have tenant ID for the component context
  const tenantId = getValidTenantId();

  // Extract customer experience data
  const { satisfaction = {}, nps = {}, feedback = {} } = data?.customerExperience || {};
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Customer Success</Typography>
        <TimeframeSelector timeframe={timeframe} onChange={onTimeframeChange} />
      </Box>
      
      {/* Top metrics cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Satisfaction Score</Typography>
              <Typography variant="h4">{satisfaction.overall?.toFixed(1) || '0.0'}%</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Overall customer satisfaction
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">NPS Score</Typography>
              <Typography variant="h4">{nps.score || 0}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Net Promoter Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Positive Sentiment</Typography>
              <Typography variant="h4">
                {feedback.sentimentAnalysis?.find(item => item.name === 'Positive')?.value || 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Feedback with positive sentiment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Promoters</Typography>
              <Typography variant="h4">
                {nps.breakdown?.find(item => item.name === 'Promoters')?.value || 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Members who are promoters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Satisfaction trends */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Satisfaction Trend</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={satisfaction.trends || []} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#8884d8" 
                    name="Satisfaction Score" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* NPS breakdown */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>NPS Breakdown</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={nps.breakdown || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name} ${value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(nps.breakdown || []).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          entry.name === 'Promoters' ? '#00C49F' : 
                          entry.name === 'Passive' ? '#FFBB28' : 
                          '#FF8042'
                        } 
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Satisfaction by category */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Satisfaction by Category</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={satisfaction.categories || []} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Score" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Sentiment analysis */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Feedback Sentiment Analysis</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={feedback.sentimentAnalysis || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name} ${value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(feedback.sentimentAnalysis || []).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          entry.name === 'Positive' ? '#00C49F' : 
                          entry.name === 'Neutral' ? '#FFBB28' : 
                          '#FF8042'
                        } 
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerSuccessAnalytics;
